import { useCallback, useEffect, useState } from "react";
import { isInstalledPwa } from "../utils/pwaUtils";

const DISMISS_KEY = "smart-farming-pwa-update-dismissed";

function isBannerDismissed(worker) {
  if (!worker?.scriptURL) return false;
  return sessionStorage.getItem(DISMISS_KEY) === worker.scriptURL;
}

/**
 * Installed PWA only: detects waiting service worker.
 * Banner can be dismissed; Notifications page keeps showing pending update until applied.
 */
export function usePwaUpdate() {
  const [registration, setRegistration] = useState(null);
  const [installed, setInstalled] = useState(isInstalledPwa);
  const [hasPendingUpdate, setHasPendingUpdate] = useState(false);
  const [bannerDismissed, setBannerDismissed] = useState(false);

  const syncPendingState = useCallback((reg) => {
    const waiting = reg?.waiting;
    const pending = Boolean(waiting && isInstalledPwa());
    setHasPendingUpdate(pending);
    setBannerDismissed(pending ? isBannerDismissed(waiting) : false);
  }, []);

  useEffect(() => {
    setInstalled(isInstalledPwa());
    const mq = window.matchMedia("(display-mode: standalone)");
    const onModeChange = () => setInstalled(isInstalledPwa());
    mq.addEventListener("change", onModeChange);
    return () => mq.removeEventListener("change", onModeChange);
  }, []);

  useEffect(() => {
    if (!installed || !("serviceWorker" in navigator)) {
      setHasPendingUpdate(false);
      setBannerDismissed(false);
      return undefined;
    }

    let refreshing = false;
    const onControllerChange = () => {
      if (refreshing) return;
      refreshing = true;
      sessionStorage.removeItem(DISMISS_KEY);
      window.location.reload();
    };
    navigator.serviceWorker.addEventListener("controllerchange", onControllerChange);

    async function listenForUpdates(reg) {
      setRegistration(reg);
      syncPendingState(reg);

      reg.addEventListener("updatefound", () => {
        const worker = reg.installing;
        if (!worker) return;
        worker.addEventListener("statechange", () => {
          if (worker.state === "installed" && navigator.serviceWorker.controller) {
            syncPendingState(reg);
          }
        });
      });
    }

    navigator.serviceWorker.ready.then(listenForUpdates).catch(() => {});

    const poll = setInterval(() => {
      navigator.serviceWorker.ready
        .then((reg) => {
          reg.update().catch(() => {});
          syncPendingState(reg);
        })
        .catch(() => {});
    }, 60000);

    return () => {
      clearInterval(poll);
      navigator.serviceWorker.removeEventListener("controllerchange", onControllerChange);
    };
  }, [installed, syncPendingState]);

  const applyUpdate = useCallback(() => {
    sessionStorage.removeItem(DISMISS_KEY);
    setBannerDismissed(false);
    if (!registration?.waiting) {
      window.location.reload();
      return;
    }
    registration.waiting.postMessage({ type: "SKIP_WAITING" });
  }, [registration]);

  const dismissBanner = useCallback(() => {
    if (registration?.waiting?.scriptURL) {
      sessionStorage.setItem(DISMISS_KEY, registration.waiting.scriptURL);
    }
    setBannerDismissed(true);
  }, [registration]);

  const showBanner = installed && hasPendingUpdate && !bannerDismissed;

  return {
    isInstalledPwa: installed,
    hasPendingUpdate: installed && hasPendingUpdate,
    showBanner,
    applyUpdate,
    dismissBanner,
  };
}
